import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { Course, CourseChapter, createCourseAsync, fetchCourseByIdAsync, updateCourseAsync, clearError, createChapterAsync, updateChapterAsync } from '../store/slices/coursesSlice';
import { useLevels } from '../hooks/useLevels';
import MinimalListEditor from './MinimalListEditor';
import ImageCropper23 from './ImageCropper23';
import './GroupForm.css';


const emptyChapter = (idx: number): CourseChapter => ({ name: '', description: '', thumbnailUrl: '', sortOrder: idx });

const CourseEditor: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentCourse, loading, error } = useAppSelector((s) => s.courses);
  const authToken = useAppSelector((s) => s.auth.token);
  const levels = useLevels();

  const [name, setName] = useState('');
  const [level, setLevel] = useState<string | null>(null);
  const [chapters, setChapters] = useState<CourseChapter[]>([]);
  const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  const [cropIdx, setCropIdx] = useState<number | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  // Tracks the source record we last synced the form from, so we can re-sync
  // during render (React's recommended pattern for adjusting state when a
  // prop/derived value changes) instead of inside an effect.
  const [syncedFrom, setSyncedFrom] = useState<{ isEdit: boolean; course: Course | null }>({
    isEdit,
    course: null,
  });

  // Clear any lingering courses errors when opening the editor
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && id) {
      dispatch(fetchCourseByIdAsync(Number(id)));
    }
  }, [dispatch, id, isEdit]);

  const editingCourse = isEdit && currentCourse && currentCourse.id === Number(id) ? currentCourse : null;

  if (editingCourse) {
    if (syncedFrom.course !== editingCourse) {
      setSyncedFrom({ isEdit: true, course: editingCourse });
      setName(editingCourse.name);
      setLevel(editingCourse.level);
      const sorted = [...(editingCourse.chapters || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setChapters(sorted);
    }
  } else if (!isEdit && syncedFrom.isEdit) {
    setSyncedFrom({ isEdit: false, course: null });
    setName('');
    setLevel(null);
    setChapters([]);
  }

  const canSave = useMemo(() => {
    if (!isEdit) return name.trim().length > 0;
    return name.trim().length > 0 && chapters.every(c => c.name.trim().length > 0);
  }, [name, chapters, isEdit]);

  const addChapter = () => {
    setChapters((prev) => [...prev, emptyChapter(prev.length)]);
  };

  const removeChapter = (index: number) => {
    setChapters((prev) => prev.filter((_, i) => i !== index).map((c, i) => ({ ...c, sortOrder: i })));
  };

  const moveChapter = (index: number, dir: -1 | 1) => {
    setChapters((prev) => {
      const newArr = [...prev];
      const target = index + dir;
      if (target < 0 || target >= newArr.length) return prev;
      const temp = newArr[index];
      newArr[index] = newArr[target];
      newArr[target] = temp;
      return newArr.map((c, i) => ({ ...c, sortOrder: i }));
    });
  };

  const updateChapterField = (index: number, field: keyof CourseChapter, value: CourseChapter[keyof CourseChapter]) => {
    setChapters((prev) => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const uploadChapterThumbnail = async (index: number, fileOrBlob: File | Blob) => {
    try {
      setUploadingIdx(index);
      const API_BASE_URL = import.meta.env.VITE_API_URL as string;
      const formData = new FormData();
      const file = fileOrBlob instanceof File ? fileOrBlob : new File([fileOrBlob], 'thumbnail.png', { type: 'image/png' });
      formData.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/courses/thumbnails`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        }
      });
      const url = res.data?.url as string;
      if (url) {
        const apiOrigin = API_BASE_URL.replace(/\/api$/, '');
        const fullUrl = /^https?:\/\//i.test(url) ? url : `${apiOrigin}${url}`;
        updateChapterField(index, 'thumbnailUrl', fullUrl);
      }
    } catch (e) {
      console.error('Thumbnail upload failed', e);
      // surface a simple error via courses slice error
      // (alternatively, we could introduce local error state)
    } finally {
      setUploadingIdx(null);
    }
  };

  const onPickChapterImage = (index: number, file: File) => {
    const url = URL.createObjectURL(file);
    setCropIdx(index);
    setCropSrc(url);
  };

  const onCropCancel = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropIdx(null);
    setCropSrc(null);
  };

  const onCropSave = async (blob: Blob) => {
    if (cropIdx == null) return;
    await uploadChapterThumbnail(cropIdx, blob);
    onCropCancel();
  };

  const saveChapter = async (index: number) => {
    if (!isEdit || !id) return;
    try {
      setSavingIdx(index);
      const ch = chapters[index];
      const data = {
        name: ch.name.trim(),
        description: ch.description || '',
        thumbnailUrl: ch.thumbnailUrl || '',
        sortOrder: ch.sortOrder ?? index
      };
      if (ch.id) {
        const res = await dispatch(updateChapterAsync({ courseId: Number(id), chapterId: ch.id, data }));
        if ('error' in res && res.error) return;
      } else {
        const res = await dispatch(createChapterAsync({ courseId: Number(id), data }));
        if ('error' in res && res.error) return;
        const createdId = createChapterAsync.fulfilled.match(res) ? res.payload.chapter.id : undefined;
        if (createdId) {
          updateChapterField(index, 'id', createdId);
        }
      }
    } finally {
      setSavingIdx(null);
    }
  };

  const handleSave = async () => {
    const payload = {
      name: name.trim(),
      level,
      chapters: chapters.map((c, idx) => ({
        id: c.id,
        name: c.name.trim(),
        description: c.description || '',
        thumbnailUrl: c.thumbnailUrl || '',
        sortOrder: idx,
      }))
    };

    if (isEdit && id) {
      const res = await dispatch(updateCourseAsync({ id: Number(id), data: payload }));
      if ('error' in res && res.error) return;
      navigate(`/courses/edit/${id}`);
    } else {
      const res = await dispatch(createCourseAsync(payload));
      if ('error' in res && res.error) return;
      const created = createCourseAsync.fulfilled.match(res) ? res.payload : undefined;
      if (created?.id) {
        navigate(`/courses/edit/${created.id}`);
      } else {
        navigate('/courses');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSave();
  };

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{isEdit ? 'Edit Course' : 'Create New Course'}</h1>
          <p>{isEdit ? 'Update course details and chapters' : 'Add a new course'}</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => navigate('/courses')}>Cancel</button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Course Information</h2>
          <div className="form-row-compact">
            <div className="form-group">
              <label htmlFor="courseName">Name *</label>
              <input
                id="courseName"
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="courseLevel">Level</label>
              <select
                id="courseLevel"
                className="form-select"
                value={level ?? ''}
                onChange={(e) => setLevel(e.target.value || null)}
              >
                <option value="">No level</option>
                {levels.map((l) => (
                  <option key={l.label} value={l.label}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {isEdit && (
          <div className="form-section">
            <div className="section-header">
              <h2>Chapters</h2>
              <button type="button" className="btn-primary" onClick={addChapter}>+ Add Chapter</button>
            </div>
            {chapters.length === 0 && <div className="muted">No chapters yet</div>}
            {chapters.map((ch, idx) => (
              <div className="schedule-item" key={idx}>
                <div className="section-header">
                  <h3>Chapter {idx + 1}</h3>
                  <div className="chapter-actions">
                    <button type="button" className="btn-secondary" onClick={() => moveChapter(idx, -1)} disabled={idx === 0}>↑</button>
                    <button type="button" className="btn-secondary" onClick={() => moveChapter(idx, 1)} disabled={idx === chapters.length - 1}>↓</button>
                    <button type="button" className="btn-remove-schedule" onClick={() => removeChapter(idx)}>×</button>
                  </div>
                </div>
                <div className="grid-12">
                  <div className="col-span-9">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        className="form-input"
                        value={ch.name}
                        onChange={(e) => updateChapterField(idx, 'name', e.target.value)}
                        maxLength={120}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <MinimalListEditor
                        value={ch.description || ''}
                        onChange={(val) => updateChapterField(idx, 'description', val)}
                      />
                    </div>
                  </div>
                  <div className="col-span-3">
                    <div className="form-group">
                      <label>Thumbnail</label>
                      <div className="thumb-preview-23">
                        {ch.thumbnailUrl ? (
                          <img src={ch.thumbnailUrl} alt="Chapter thumbnail" />
                        ) : (
                          <div className="thumb-preview-empty">No thumbnail yet</div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                        <input
                          id={`chapter-file-${idx}`}
                          className="hidden-file-input"
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) onPickChapterImage(idx, file);
                            // reset value so selecting the same file again re-triggers change
                            if (e.currentTarget) e.currentTarget.value = '';
                          }}
                          disabled={uploadingIdx === idx}
                        />
                        <button
                          type="button"
                          className="btn-upload"
                          disabled={uploadingIdx === idx}
                          onClick={() => document.getElementById(`chapter-file-${idx}`)?.click()}
                        >
                          {uploadingIdx === idx ? 'Uploading…' : 'Upload Image'}
                        </button>
                        {ch.thumbnailUrl && (
                          <button
                            type="button"
                            className="btn-icon-danger"
                            aria-label="Remove thumbnail"
                            title="Remove thumbnail"
                            onClick={() => updateChapterField(idx, 'thumbnailUrl', '')}
                          >
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-9 0h10l-1 14a2 2 0 01-2 2H9a2 2 0 01-2-2L6 6zm3 4v8m4-8v8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </button>
                        )}
                      </div>
                      {uploadingIdx === idx && <div className="muted">Uploading...</div>}
                    </div>
                  </div>
                </div>
                <div className="section-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn-success" disabled={savingIdx === idx || uploadingIdx === idx || !ch.name.trim()} onClick={() => saveChapter(idx)}>
                    {savingIdx === idx ? 'Saving...' : (ch.id ? 'Save Chapter' : 'Create Chapter')}
                  </button>
                </div>
              </div>
            ))}
            {chapters.length > 0 && (
              <div className="section-footer">
                <button type="button" className="btn-primary" onClick={addChapter}>+ Add Chapter</button>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/courses')}>Cancel</button>
          <button type="submit" className="btn-success" disabled={!canSave || loading}>{isEdit ? 'Save Changes' : 'Create Course'}</button>
        </div>
      </form>
      {cropSrc && cropIdx != null && (
        <ImageCropper23 src={cropSrc} width={200} height={300} onCancel={onCropCancel} onSave={onCropSave} />
      )}
    </div>
  );
};

export default CourseEditor;
